import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Trash2, Plus, X, Save, AlertCircle, Pencil, Search, Check } from "lucide-react";
import toast from 'react-hot-toast';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import type { ItemCatalogo, RutinaDetalle } from '../../types/planMaestro';
import { Loader2 } from 'lucide-react';

// Función auxiliar para persistencia de caché en el Iframe (sessionStorage)
const ST_KEY_CATALOG = 'vilox_cache_catalog';

const getCache = (key: string) => {
    try {
        const stored = sessionStorage.getItem(key);
        return stored ? JSON.parse(stored) : null;
    } catch { return null; }
};

const setCache = (key: string, data: any) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) { console.warn("Cache storage full", e); }
};

interface PlanMaestroFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    planId?: number;
}

interface RoutineFormData {
    id_bd?: number; // ID real de base de datos
    nombre: string;
    tipo: 'M1' | 'M2' | 'M3' | 'M4' | 'M5' | 'POD';
    frecuencia_km: number;
    frecuencia_horas?: number;
    frecuencia_meses?: number;
}

interface PlanFormData {
    nombre: string;
    descripcion: string;
    tipo_operacion: 'CARRETERA' | 'FUERA_CARRETERA' | 'MIXTO';
    modelo_vehiculo: string;
    motor: string;
    transmision: string;
    rutinas: RoutineFormData[];
}

type TabType = 'info' | 'rutinas' | 'matriz' | 'items';

export const PlanMaestroForm: React.FC<PlanMaestroFormProps> = ({ open, onClose, onSuccess, planId }) => {
    const [activeTab, setActiveTab] = useState<TabType>('info');
    const [isLoading, setIsLoading] = useState(false);

    // Estados para la matriz
    const [catalogoItems, setCatalogoItems] = useState<ItemCatalogo[]>([]);
    const [planDetalles, setPlanDetalles] = useState<Record<number, RutinaDetalle[]>>({});
    const [matrixChanges, setMatrixChanges] = useState<Record<string, { aplica: boolean, cantidad: number }>>({});
    const [searchTerm, setSearchTerm] = useState("");

    // Estados para gestión de items
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<Partial<ItemCatalogo>>({});
    const [itemIsSaving, setItemIsSaving] = useState(false);

    // Selector de Catálogo Global
    const [isCatalogPickerOpen, setIsCatalogPickerOpen] = useState(false);
    const [catalogPickerType, setCatalogPickerType] = useState<'system' | 'company'>('system');
    const [pickerItems, setPickerItems] = useState<ItemCatalogo[]>([]);
    const [pickerSearch, setPickerSearch] = useState("");

    // Estado para confirmación de eliminación
    const [itemToDelete, setItemToDelete] = useState<ItemCatalogo | null>(null);

    const { register, control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<PlanFormData>({
        defaultValues: {
            tipo_operacion: 'CARRETERA',
            rutinas: [
                { nombre: 'M1 - 5000 KM', tipo: 'M1', frecuencia_km: 5000 },
                { nombre: 'M2 - 10000 KM', tipo: 'M2', frecuencia_km: 10000 },
                { nombre: 'M3 - 20000 KM', tipo: 'M3', frecuencia_km: 20000 },
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "rutinas"
    });

    const loadCatalog = async (force = false) => {
        try {
            const cached = getCache(ST_KEY_CATALOG);
            if (cached && !force) {
                setCatalogoItems(cached);
                return;
            }
            const response = await maintenanceService.getItemsCatalogo();
            if (response.success && response.data) {
                setCache(ST_KEY_CATALOG, response.data);
                setCatalogoItems(response.data as ItemCatalogo[]);
            }
        } catch (error) {
            console.error("Error al cargar catálogo:", error);
        }
    };

    // Cargar datos
    useEffect(() => {
        if (open) {
            try {
                sessionStorage.removeItem(ST_KEY_CATALOG);
            } catch (e) {
                console.warn(e);
            }
            const init = async () => {
                setIsLoading(true);
                try {
                    if (planId) {
                        setMatrixChanges({}); // Reseteamos cambios pendientes
                        const response = await maintenanceService.getPlanMaestroById(planId);

                        if (response.success && response.data) {
                            const { plan, rutinas, detalles, items } = response.data as any; // items vienen filtrados del backend

                            if (detalles) setPlanDetalles(detalles);
                            if (items) setCatalogoItems(items); // Usar items del plan

                            if (plan) {
                                setValue('nombre', plan.nombre);
                                setValue('descripcion', plan.descripcion || '');
                                setValue('tipo_operacion', plan.tipo_operacion);
                                setValue('modelo_vehiculo', plan.modelo_vehiculo || '');
                                setValue('motor', plan.motor || '');
                                setValue('transmision', plan.transmision || '');
                            }

                            if (rutinas && rutinas.length > 0) {
                                const mappedRoutines: RoutineFormData[] = rutinas.map((r: any) => ({
                                    id_bd: r.id, // Mapeamos ID real a id_bd
                                    nombre: r.nombre,
                                    tipo: r.tipo,
                                    frecuencia_km: r.frecuencia_km || 0,
                                    frecuencia_horas: r.frecuencia_horas,
                                    frecuencia_meses: r.frecuencia_meses
                                }));
                                setValue('rutinas', mappedRoutines);
                            } else if (plan.rutinas && plan.rutinas.length > 0) {
                                const mappedRoutines: RoutineFormData[] = plan.rutinas.map((r: any) => ({
                                    id_bd: r.id,
                                    nombre: r.nombre,
                                    tipo: r.tipo,
                                    frecuencia_km: r.frecuencia_km || 0,
                                    frecuencia_horas: r.frecuencia_horas,
                                    frecuencia_meses: r.frecuencia_meses
                                }));
                                setValue('rutinas', mappedRoutines);
                            }
                        }
                    } else {
                        // Reset form for new plan
                        await loadCatalog();
                        reset({
                            tipo_operacion: 'CARRETERA',
                            rutinas: [
                                { nombre: 'M1 - 5000 KM', tipo: 'M1', frecuencia_km: 5000, frecuencia_horas: 250, frecuencia_meses: 0 },
                                { nombre: 'M2 - 10000 KM', tipo: 'M2', frecuencia_km: 10000, frecuencia_horas: 500, frecuencia_meses: 0 },
                                { nombre: 'M3 - 20000 KM', tipo: 'M3', frecuencia_km: 20000, frecuencia_horas: 1000, frecuencia_meses: 0 },
                                { nombre: 'M1 - 25000 KM', tipo: 'M1', frecuencia_km: 25000, frecuencia_horas: 1250, frecuencia_meses: 0 },
                            ]
                        });
                        setPlanDetalles({});
                        setMatrixChanges({});
                    }
                } catch (error) {
                    console.error("Error loading data:", error);
                    toast.error("Error al cargar datos");
                } finally {
                    setIsLoading(false);
                }
            };
            init();
        }
    }, [planId, open, setValue, reset]);

    const toggleDetalle = (rutinaId: number, itemId: number, checked: boolean) => {
        const key = `${rutinaId}-${itemId}`;
        setMatrixChanges(prev => ({
            ...prev,
            [key]: { aplica: checked, cantidad: 1 } // Por defecto cantidad 1
        }));
    };

    const updateCantidad = (rutinaId: number, itemId: number, qty: number) => {
        const key = `${rutinaId}-${itemId}`;
        const safeQty = isNaN(qty) ? 1 : qty;

        setMatrixChanges(prev => {
            const previousState = prev[key];
            const isDbSet = planDetalles[rutinaId]?.some(d => d.plan_item_id === itemId && d.aplica);
            const currentAplica = previousState ? previousState.aplica : (isDbSet || false);

            return {
                ...prev,
                [key]: { aplica: currentAplica, cantidad: safeQty }
            };
        });
    };

    const deletePlanItem = async (itemId: number) => {
        if (!planId) return;
        try {
            await maintenanceService.deletePlanItem(planId, itemId);
            setCatalogoItems(prev => prev.filter(i => i.id !== itemId));

            const newMatrixChanges = { ...matrixChanges };
            Object.keys(newMatrixChanges).forEach(key => {
                if (key.endsWith(`-${itemId}`)) {
                    delete newMatrixChanges[key];
                }
            });
            setMatrixChanges(newMatrixChanges);

            toast.success("Ítem eliminado del plan");
            setItemToDelete(null);
        } catch (error) {
            console.error("Error deleting item:", error);
            toast.error("Error al eliminar el ítem");
        }
    };

    const openItemModal = (item?: ItemCatalogo) => {
        setCurrentItem(item || { activo: true } as any); // Cast to any effectively or add activo to type if needed
        setIsItemModalOpen(true);
    };

    const handleSaveItem = async (e: React.FormEvent) => {
        e.preventDefault();
        setItemIsSaving(true);
        try {
            // Validaciones básicas
            if (!currentItem.codigo_interno || !currentItem.nombre || !currentItem.categoria || !currentItem.unidad_medida) {
                toast.error("Complete los campos obligatorios (*)");
                return;
            }

            if (currentItem.id) {
                // Edición
                if (currentItem.item_catalogo_id && planId) {
                    const isSystemItem = !currentItem.empresa_id;

                    if (isSystemItem) {
                        // "Sistema -> Custom": Crear nuevo en catálogo y actualizar relación en plan
                        const itemData = {
                            nombre: currentItem.nombre,
                            codigo_interno: currentItem.codigo_interno,
                            descripcion: currentItem.descripcion,
                            categoria: currentItem.categoria,
                            unidad_medida: currentItem.unidad_medida,
                            precio_unitario: currentItem.precio_unitario,
                            tipo: currentItem.tipo,
                            activo: true
                        };

                        const createRes = await maintenanceService.createItemCatalogo(itemData);

                        if (createRes.success && createRes.data) {
                            const newItem = createRes.data;
                            // Actualizar el PlanItem para que apunte al nuevo item del catálogo y tenga los nuevos datos
                            await maintenanceService.updatePlanItem(planId, currentItem.id!, {
                                item_catalogo_id: newItem.id,
                                ...itemData,
                                es_personalizado: true
                            });

                            toast.success("Item de sistema personalizado como propio");
                        }
                    } else {
                        // Actualizar ítem existente en catálogo y en el plan
                        await maintenanceService.updateItemCatalogo(currentItem.item_catalogo_id, currentItem);
                        await maintenanceService.updatePlanItem(planId, currentItem.id, currentItem);
                        toast.success("Ítem actualizado");
                    }

                    // Recarga total para asegurar consistencia
                    const response = await maintenanceService.getPlanMaestroById(planId);
                    if (response.success && response.data) {
                        const { detalles, items } = response.data as any;
                        if (items) setCatalogoItems(items);
                        if (detalles) setPlanDetalles(detalles);
                    }
                } else {
                    toast.error("Error al identificar el ítem o el plan");
                }
            } else {
                // Creación
                const res = await maintenanceService.createItemCatalogo(currentItem);

                if (planId && res.success && res.data) {
                    const catalogItem = res.data;
                    const linkRes = await maintenanceService.addPlanItem(planId, catalogItem.id);

                    if (linkRes.success && linkRes.data) {
                        setCatalogoItems(prev => [...prev, linkRes.data]);
                        toast.success("Ítem creado y agregado al plan");
                    }
                } else {
                    toast.success("Ítem creado en catálogo");
                    await loadCatalog();
                }
            }
            setIsItemModalOpen(false);
        } catch (error: any) {
            console.error("Save item error:", error);
            let errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Error desconocido";

            // Si hay errores de validación específicos (status 422)
            if (error.response?.data?.errors) {
                const detailedErrors = Object.values(error.response.data.errors).flat().join(", ");
                errorMsg = `${errorMsg}: ${detailedErrors}`;
            }

            toast.error(errorMsg);
        } finally {
            setItemIsSaving(false);
        }
    };

    const handleOpenCatalogPicker = async (type: 'system' | 'company' = 'system') => {
        setIsCatalogPickerOpen(true);
        setCatalogPickerType(type);
        setPickerSearch("");
        try {
            const cached = getCache(ST_KEY_CATALOG);
            if (cached) {
                setPickerItems(cached);
                return;
            }
            const res = await maintenanceService.getItemsCatalogo();
            if (res.success && res.data) {
                setCache(ST_KEY_CATALOG, res.data);
                setPickerItems(res.data as ItemCatalogo[]);
            }
        } catch (error) {
            console.error(error);
            toast.error("Error al cargar el catálogo");
        }
    };

    const handleAddFromCatalog = async (item: ItemCatalogo) => {
        if (!planId) return;
        try {
            if (catalogoItems.some(i => i.item_catalogo_id === item.id)) {
                toast.error("El ítem ya está agregado al plan");
                return;
            }

            const res = await maintenanceService.addPlanItem(planId, item.id);
            if (res.success && res.data) {
                setCatalogoItems(prev => [...prev, res.data]);
                toast.success("Ítem agregado al plan");
            } else {
                toast.error(res.message || "Error al agregar");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error al vincular ítem");
        }
    };

    const onSubmit = async (data: PlanFormData) => {
        try {
            // Preparar Rutinas (con mapeo inverso de id_bd a id)
            const rutinasFormatted = data.rutinas.map(r => ({
                ...r,
                id: r.id_bd // Restaurar el ID para que el backend sepa actualizar
            }));

            // Preparar Detalles (Matriz)
            const detallesFormatted = Object.entries(matrixChanges).map(([key, value]) => {
                const [rutinaId, itemId] = key.split('-').map(Number);
                return {
                    rutina_id: rutinaId,
                    plan_item_id: itemId, // Now using plan_item_id
                    aplica: value.aplica,
                    cantidad: value.cantidad
                };
            });

            const planData: any = {
                ...data,
                rutinas: rutinasFormatted,
                detalles: detallesFormatted.length > 0 ? detallesFormatted : undefined
            };

            if (planId) {
                await maintenanceService.updatePlanMaestro(planId, planData);
                toast.success("Plan Maestro actualizado exitosamente");
            } else {
                await maintenanceService.createPlanMaestro(planData);
                toast.success("Plan Maestro creado exitosamente");
            }
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            toast.error("Error al guardar: " + (error.response?.data?.message || error.message));
        }
    };



    // Filtros de búsqueda y asociación - Optimizados con useMemo
    const filteredItems = React.useMemo(() => {
        return catalogoItems.filter(item => {
            const term = searchTerm.toLowerCase();
            return item.nombre.toLowerCase().includes(term) ||
                item.codigo_interno.toLowerCase().includes(term) ||
                item.categoria.toLowerCase().includes(term);
        });
    }, [catalogoItems, searchTerm]);

    if (!open) return null;

    const TabButton = ({ id, label }: { id: TabType, label: string }) => (
        <button
            type="button"
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${activeTab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
        >
            {label}
        </button>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-950 rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col border border-slate-200 dark:border-slate-800">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 bg-white sticky top-0 rounded-t-lg">
                    <div className="flex items-center gap-2">
                        <div className="bg-blue-100 p-2 rounded-lg">
                            <Save className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">
                                {planId ? 'Editar Plan Maestro' : 'Nuevo Plan Maestro'}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                                {planId ? `Editando plan #${planId}` : 'Creando nuevo plan de mantenimiento'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 px-4 border-b border-slate-200 bg-slate-50/50">
                    <TabButton id="info" label="Información del Plan" />
                    <TabButton id="rutinas" label="Rutinas" />
                    <TabButton id="matriz" label="Matriz de Actividades" />
                    <TabButton id="items" label="Ítems del Catálogo" />
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden relative">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
                            <p className="text-sm font-medium text-gray-500">Cargando detalles del plan...</p>
                        </div>
                    ) : (
                        <form id="planForm" onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
                            <div className="flex-1 overflow-y-auto p-6 scroll-smooth">

                                {/* Tab: Información */}
                                {activeTab === 'info' && (
                                    <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-300">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2 col-span-1 md:col-span-2">
                                                <Label htmlFor="nombre" className="text-slate-700">Nombre del Plan</Label>
                                                <Input id="nombre" {...register("nombre", { required: true })} placeholder="Ej: Plan Volquete Volvo FH" className="font-medium" />
                                                {errors.nombre && <span className="text-red-500 text-xs">Requerido</span>}
                                            </div>

                                            <div className="space-y-2 col-span-1 md:col-span-2">
                                                <Label htmlFor="tipo_operacion" className="text-slate-700">Tipo de Operación</Label>
                                                <select
                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                                    {...register("tipo_operacion")}
                                                >
                                                    <option value="CARRETERA">CARRETERA</option>
                                                    <option value="FUERA_CARRETERA">FUERA DE CARRETERA</option>
                                                    <option value="MIXTO">MIXTO</option>
                                                </select>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="modelo_vehiculo">Modelo Vehículo</Label>
                                                <Input id="modelo_vehiculo" {...register("modelo_vehiculo")} placeholder="Ej: HFC3251KN" />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="motor">Motor</Label>
                                                <Input id="motor" {...register("motor")} placeholder="Ej: Cummins ISGe-5 430" />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="transmision">Transmisión</Label>
                                                <Input id="transmision" {...register("transmision")} placeholder="Ej: Fast Gear 12JSDX220T-B" />
                                            </div>

                                            <div className="space-y-2 col-span-1 md:col-span-2">
                                                <Label htmlFor="descripcion">Descripción (Opcional)</Label>
                                                <textarea
                                                    id="descripcion"
                                                    {...register("descripcion")}
                                                    className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                                                    placeholder="Detalles adicionales del plan..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Tab: Rutinas */}
                                {activeTab === 'rutinas' && (
                                    <div className="space-y-4 animate-in fade-in duration-300">
                                        <div className="flex justify-between items-center mb-4">
                                            <div>
                                                <h3 className="text-lg font-medium">Configuración de Rutinas</h3>
                                                <p className="text-sm text-gray-500">Defina los intervalos de mantenimiento.</p>
                                            </div>
                                            <Button type="button" variant="outline" size="sm" onClick={() => append({ nombre: 'Nueva Rutina', tipo: 'M1', frecuencia_km: 0, frecuencia_horas: 0, frecuencia_meses: 0 })}>
                                                <Plus className="w-4 h-4 mr-2" /> Agregar Nueva Rutina
                                            </Button>
                                        </div>

                                        <div className="space-y-3">
                                            {fields.map((field, index) => (
                                                <div key={field.id} className="flex flex-col sm:flex-row gap-3 items-end p-4 border border-gray-100 rounded-lg bg-slate-50/50 hover:bg-slate-50 transition-colors">
                                                    <div className="flex-1 w-full sm:w-auto space-y-1.5">
                                                        <Label className="text-xs text-gray-500 font-semibold">Nombre Rutina</Label>
                                                        <Input {...register(`rutinas.${index}.nombre` as const, { required: true })} placeholder="Ej: M1 - 5000 KM" className="bg-white" />
                                                    </div>
                                                    <div className="w-full sm:w-32 space-y-1.5">
                                                        <Label className="text-xs text-gray-500 font-semibold">Tipo</Label>
                                                        <select
                                                            className="flex h-10 w-full rounded-md border border-input bg-white px-2 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                            {...register(`rutinas.${index}.tipo` as const)}
                                                        >
                                                            <option value="M1">M1</option>
                                                            <option value="M2">M2</option>
                                                            <option value="M3">M3</option>
                                                            <option value="M4">M4</option>
                                                            <option value="M5">M5</option>
                                                            <option value="POD">POD</option>
                                                        </select>
                                                    </div>
                                                    <div className="w-full sm:w-32 space-y-1.5">
                                                        <Label className="text-xs text-gray-500 font-semibold">Km</Label>
                                                        <Input type="number" {...register(`rutinas.${index}.frecuencia_km` as const, { valueAsNumber: true })} placeholder="KM" className="bg-white" />
                                                    </div>
                                                    <div className="w-full sm:w-32 space-y-1.5">
                                                        <Label className="text-xs text-gray-500 font-semibold">Horas</Label>
                                                        <Input type="number" {...register(`rutinas.${index}.frecuencia_horas` as const, { valueAsNumber: true })} placeholder="Hrs" className="bg-white" />
                                                    </div>
                                                    <div className="w-full sm:w-32 space-y-1.5">
                                                        <Label className="text-xs text-gray-500 font-semibold">Meses</Label>
                                                        <Input type="number" {...register(`rutinas.${index}.frecuencia_meses` as const, { valueAsNumber: true })} placeholder="Meses" className="bg-white" />
                                                    </div>
                                                    <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:bg-red-50 hover:text-red-600 mb-[2px]" onClick={() => remove(index)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Tab: Matriz de Actividades */}
                                {activeTab === 'matriz' && (
                                    <div className="animate-in fade-in duration-300">
                                        {!planId ? (
                                            <div className="flex flex-col items-center justify-center p-12 text-center">
                                                <div className="bg-blue-50 rounded-full p-4 mb-4">
                                                    <AlertCircle className="w-8 h-8 text-blue-500" />
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Guarde el plan primero</h3>
                                                <p className="text-sm text-gray-500 max-w-md mb-6">
                                                    Para configurar la matriz de actividades, primero debe guardar la información básica y las rutinas del plan.
                                                </p>
                                                <Button onClick={() => handleSubmit(onSubmit)()}>
                                                    Guardar Plan Inicial
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center px-1">
                                                    <div>
                                                        <h3 className="text-lg font-medium">Matriz de Configuración</h3>
                                                        <p className="text-xs text-gray-500">Marque las actividades que aplican a cada rutina.</p>
                                                    </div>
                                                </div>

                                                <div className="border rounded-lg shadow-sm overflow-hidden bg-white">
                                                    <div className="overflow-x-auto max-h-[600px]">
                                                        <table className="w-full text-sm text-left border-collapse">
                                                            <thead className="bg-gray-50 text-gray-700 text-xs uppercase sticky top-0 z-20 shadow-sm">
                                                                <tr>
                                                                    <th className="sticky left-0 bg-gray-50 z-30 w-32 px-4 py-3 border-b text-left min-w-[120px]">Categoría</th>
                                                                    <th className="sticky left-[120px] bg-gray-50 z-30 w-64 px-4 py-3 border-b border-r text-left min-w-[250px]">Descripción</th>
                                                                    {fields.filter(f => f.id_bd).map((rutina: any) => (
                                                                        <th key={rutina.id} className="px-2 py-3 border-b text-center min-w-[80px] bg-gray-50">
                                                                            <div className="flex flex-col items-center gap-1">
                                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${rutina.tipo === 'M1' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                                                                    rutina.tipo === 'M2' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                                                                        rutina.tipo === 'M3' ? 'bg-red-100 text-red-800 border-red-200' :
                                                                                            rutina.tipo === 'M4' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                                                                                                rutina.tipo === 'M5' ? 'bg-indigo-100 text-indigo-800 border-indigo-200' :
                                                                                                    rutina.tipo === 'POD' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                                                                                                        'bg-gray-100 text-gray-800'
                                                                                    }`}>
                                                                                    {rutina.tipo}
                                                                                </span>
                                                                                <div className="flex flex-col items-center gap-0.5 mt-1">
                                                                                    {rutina.frecuencia_km > 0 && (
                                                                                        <span className="text-[9px] text-slate-500 font-bold whitespace-nowrap bg-white px-1 rounded shadow-sm border border-slate-100">
                                                                                            {rutina.frecuencia_km?.toLocaleString()} KM
                                                                                        </span>
                                                                                    )}
                                                                                    {rutina.frecuencia_horas > 0 && (
                                                                                        <span className="text-[9px] text-amber-600 font-bold whitespace-nowrap bg-amber-50 px-1 rounded shadow-sm border border-amber-100">
                                                                                            {rutina.frecuencia_horas?.toLocaleString()} HRS
                                                                                        </span>
                                                                                    )}
                                                                                    {rutina.frecuencia_meses > 0 && (
                                                                                        <span className="text-[9px] text-indigo-600 font-bold whitespace-nowrap bg-indigo-50 px-1 rounded shadow-sm border border-indigo-100">
                                                                                            {rutina.frecuencia_meses?.toLocaleString()} {rutina.frecuencia_meses === 1 ? 'MES' : 'MESES'}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-100">
                                                                {(() => {
                                                                    // Helper para verificar si un item está activo en alguna rutina del plan actual
                                                                    // Mostrar todos los ítems vinculados al plan
                                                                    const activeItems = catalogoItems;

                                                                    if (activeItems.length === 0) {
                                                                        return (
                                                                            <tr>
                                                                                <td colSpan={fields.filter(f => f.id_bd).length + 2} className="px-4 py-8 text-center text-gray-500">
                                                                                    <div className="flex flex-col items-center justify-center gap-2">
                                                                                        <span>No hay actividades configuradas.</span>
                                                                                        <span className="text-xs">Vaya a la pestaña "Ítems del Catálogo" y asigne items a las rutinas (M1, M2...) usando los botones.</span>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }

                                                                    // 2. Obtener categorías únicas de los items ACTIVOS
                                                                    const uniqueCategories = Array.from(new Set(activeItems.map(i => i.categoria)));

                                                                    return uniqueCategories.map((category) => (
                                                                        <React.Fragment key={category}>
                                                                            {activeItems
                                                                                .filter(i => i.categoria === category)
                                                                                .map((item, index, arr) => {
                                                                                    const isFirstInCategory = index === 0;
                                                                                    return (
                                                                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                                                                                            {isFirstInCategory && (
                                                                                                <td
                                                                                                    className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-purple-600 text-xs align-top border-r"
                                                                                                    rowSpan={arr.length}
                                                                                                >
                                                                                                    {category}
                                                                                                </td>
                                                                                            )}
                                                                                            {!isFirstInCategory && <td className="sticky left-0 z-10 bg-white border-r hidden"></td>}

                                                                                            <td className="sticky left-[120px] z-10 bg-white px-4 py-2 border-r group-hover:bg-gray-50">
                                                                                                <div className="font-medium text-gray-800">{item.nombre}</div>
                                                                                                <div className="text-[10px] text-gray-400">{item.codigo_interno}</div>
                                                                                            </td>

                                                                                            {fields.filter(f => f.id_bd).map((rutina: any) => {
                                                                                                const matrixKey = `${rutina.id_bd}-${item.id}`;
                                                                                                // Verificar si está marcado: prioridad al cambio local, sino usar DB
                                                                                                const isChecked = matrixChanges[matrixKey] !== undefined
                                                                                                    ? matrixChanges[matrixKey].aplica
                                                                                                    : (planDetalles[rutina.id_bd!]?.find(d => d.plan_item_id === item.id)?.aplica ?? false);

                                                                                                return (
                                                                                                    <td key={rutina.id} className="px-2 py-2 text-center border-r border-dashed last:border-r-0">
                                                                                                        <div className="flex justify-center">
                                                                                                            <div
                                                                                                                onClick={() => toggleDetalle(rutina.id_bd!, item.id, !isChecked)}
                                                                                                                className={`w-6 h-6 rounded-md border flex items-center justify-center cursor-pointer transition-all duration-200 transform active:scale-95 ${isChecked
                                                                                                                    ? 'bg-emerald-500 border-emerald-600 text-white shadow-sm ring-4 ring-emerald-500/10'
                                                                                                                    : 'bg-white border-emerald-200 text-transparent hover:border-emerald-400 hover:bg-emerald-50/50'
                                                                                                                    }`}
                                                                                                                title={isChecked ? "Quitar de rutina" : "Agregar a rutina"}
                                                                                                            >
                                                                                                                {isChecked && <Check size={16} strokeWidth={4} className="animate-in zoom-in-50 duration-200" />}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                        </React.Fragment>
                                                                    ));
                                                                })()}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Tab: Catálogo de Ítems */}
                                {activeTab === 'items' && (
                                    <div className="space-y-4 animate-in fade-in duration-300">
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-medium">Catálogo de Ítems</h3>
                                                    <div className="flex gap-2">
                                                        <span className="bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm uppercase tracking-wider">
                                                            {catalogoItems.length} ÍTEMS
                                                        </span>
                                                        <span className="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm uppercase tracking-wider">
                                                            {fields.length} RUTINAS
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-gray-500">Gestione los repuestos y servicios disponibles para este plan.</p>
                                            </div>
                                            <div className="flex gap-2 w-full md:w-auto">
                                                <div className="relative flex-1 md:w-64">
                                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                                                    <Input
                                                        placeholder="Buscar por nombre, código..."
                                                        className="pl-9 h-9"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </div>
                                                <Button type="button" variant="outline" size="sm" className="border-slate-200" onClick={() => handleOpenCatalogPicker('system')}>
                                                    <Search className="w-4 h-4 mr-2" /> Buscar en Catálogo
                                                </Button>
                                                <Button type="button" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openItemModal()}>
                                                    <Plus className="w-4 h-4 mr-2" /> Nuevo Ítem
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="border rounded-lg shadow-sm bg-white overflow-hidden">
                                            <div className="overflow-x-auto max-h-[600px]">
                                                <table className="w-full text-sm text-left">
                                                    <thead className="bg-gray-50 text-gray-700 text-xs uppercase sticky top-0 z-10">
                                                        <tr>
                                                            <th className="px-4 py-3 border-b">Código</th>
                                                            <th className="px-4 py-3 border-b">Nombre</th>
                                                            <th className="px-4 py-3 border-b">Categoría</th>
                                                            <th className="px-4 py-3 border-b">Unidad</th>
                                                            <th className="px-4 py-3 border-b">Origen</th>
                                                            <th className="px-4 py-3 border-b">Asignado a</th>
                                                            <th className="px-4 py-3 border-b text-right">Precio Ref.</th>
                                                            <th className="px-4 py-3 border-b text-center">Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {filteredItems.map((item) => {
                                                            return (
                                                                <tr key={item.id} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-2 font-medium text-gray-900 w-32">{item.codigo_interno}</td>
                                                                    <td className="px-4 py-2 min-w-[200px]">
                                                                        <div className="font-medium text-gray-800">{item.nombre}</div>
                                                                        <div className="text-[10px] text-gray-400">{item.descripcion}</div>
                                                                    </td>
                                                                    <td className="px-4 py-2">
                                                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
                                                                            {item.categoria}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-2 text-gray-500">{item.unidad_medida}</td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${!item.empresa_id ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                                            {!item.empresa_id ? 'Sistema' : 'Propio'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-2">
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {fields.filter(f => f.id_bd).map((rutina: any) => {
                                                                                const matrixKey = `${rutina.id_bd}-${item.id}`;
                                                                                const isLocallySet = matrixChanges[matrixKey]?.aplica;
                                                                                const dbDetail = planDetalles[rutina.id_bd]?.find(d => d.plan_item_id === item.id);
                                                                                const isDbSet = dbDetail?.aplica;
                                                                                const isActive = matrixChanges[matrixKey] !== undefined ? isLocallySet : isDbSet;

                                                                                const currentQty = matrixChanges[matrixKey]?.cantidad ?? dbDetail?.cantidad ?? 1;

                                                                                return (
                                                                                    <div key={rutina.id} className="flex items-center gap-1">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => toggleDetalle(rutina.id_bd!, item.id, !isActive)}
                                                                                            className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-all cursor-pointer select-none ${isActive
                                                                                                ? rutina.tipo === 'M1' ? 'bg-blue-100 text-blue-800 border-blue-300 shadow-sm' :
                                                                                                    rutina.tipo === 'M2' ? 'bg-orange-100 text-orange-800 border-orange-300 shadow-sm' :
                                                                                                        rutina.tipo === 'M3' ? 'bg-red-100 text-red-800 border-red-300 shadow-sm' :
                                                                                                            'bg-green-100 text-green-800 border-green-300 shadow-sm'
                                                                                                : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300 hover:text-gray-600'
                                                                                                }`}
                                                                                            title={`Asignar a ${rutina.nombre}`}
                                                                                        >
                                                                                            {rutina.tipo}
                                                                                        </button>
                                                                                        {isActive && (
                                                                                            <input
                                                                                                type="number"
                                                                                                min="0.1"
                                                                                                step="0.1"
                                                                                                className="w-12 px-1 py-0.5 text-[10px] border border-gray-300 rounded text-center focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                                                                                value={currentQty}
                                                                                                onChange={(e) => updateCantidad(rutina.id_bd!, item.id, parseFloat(e.target.value))}
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-2 text-right text-gray-600 w-28">
                                                                        {item.precio_unitario ? `$${Number(item.precio_unitario).toFixed(2)}` : '-'}
                                                                    </td>
                                                                    <td className="px-4 py-2 text-center w-20">
                                                                        <div className="flex justify-center gap-2">
                                                                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openItemModal(item)}>
                                                                                <Pencil className="w-4 h-4" />
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-8 w-8 text-rose-600 hover:bg-rose-50"
                                                                                onClick={() => setItemToDelete(item)}
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </Button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {filteredItems.length === 0 && (
                                                            <tr>
                                                                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                                                    {catalogoItems.length === 0 ? "No hay ítems registrados." : "No se encontraron ítems."}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>

                            {/* Footer Actions */}
                            <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-900 sticky bottom-0 rounded-b-lg">
                                <div className="text-xs text-gray-400">
                                    {isSubmitting ? 'Guardando cambios...' : 'Listo'}
                                </div>
                                <div className="flex gap-3">
                                    <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={isSubmitting} className="min-w-[140px] bg-blue-600 hover:bg-blue-700">
                                        {isSubmitting ? (
                                            <div className="flex items-center">
                                                <span className="animate-spin mr-2">⏳</span> Guardando...
                                            </div>
                                        ) : (
                                            <div className="flex items-center">
                                                <Save className="w-4 h-4 mr-2" /> Guardar Cambios
                                            </div>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                {/* Modal de Items (Nested) */}
                {isItemModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col border border-gray-200">
                            <div className="flex justify-between items-center p-4 border-b">
                                <h3 className="text-lg font-bold">{currentItem.id ? 'Editar Ítem' : 'Nuevo Ítem'}</h3>
                                <button onClick={() => setIsItemModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-full">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <form onSubmit={handleSaveItem} className="p-4 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Código Interno *</Label>
                                        <Input
                                            value={currentItem.codigo_interno || ''}
                                            onChange={e => setCurrentItem({ ...currentItem, codigo_interno: e.target.value })}
                                            placeholder="Ej: FLT-001"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Categoría *</Label>
                                        <Input
                                            value={currentItem.categoria || ''}
                                            onChange={e => setCurrentItem({ ...currentItem, categoria: e.target.value.toUpperCase() })}
                                            placeholder="Ej: FILTROS"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Nombre del Ítem *</Label>
                                    <Input
                                        value={currentItem.nombre || ''}
                                        onChange={e => setCurrentItem({ ...currentItem, nombre: e.target.value })}
                                        placeholder="Ej: Filtro de Aceite"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Unidad *</Label>
                                        <select
                                            className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            value={currentItem.unidad_medida || ''}
                                            onChange={e => setCurrentItem({ ...currentItem, unidad_medida: e.target.value })}
                                            required
                                        >
                                            <option value="">Seleccione</option>
                                            <option value="UND">UNIDAD (UND)</option>
                                            <option value="GL">GALÓN (GL)</option>
                                            <option value="L">LITRO (L)</option>
                                            <option value="JGO">JUEGO (JGO)</option>
                                            <option value="KIT">KIT</option>
                                            <option value="SERV">SERVICIO</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Precio Referencial</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={currentItem.precio_unitario || ''}
                                            onChange={e => setCurrentItem({ ...currentItem, precio_unitario: parseFloat(e.target.value) })}
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Descripción Adicional</Label>
                                    <textarea
                                        className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[60px]"
                                        value={currentItem.descripcion || ''}
                                        onChange={e => setCurrentItem({ ...currentItem, descripcion: e.target.value })}
                                    />
                                </div>
                                <div className="pt-2 flex justify-end gap-2">
                                    <Button type="button" variant="outline" onClick={() => setIsItemModalOpen(false)}>Cancelar</Button>
                                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={itemIsSaving}>
                                        {itemIsSaving ? 'Guardando...' : 'Guardar Ítem'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal de Selector de Catálogo */}
                {isCatalogPickerOpen && (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col border border-gray-200 h-[80vh]">
                            <div className="flex justify-between items-center p-4 border-b">
                                <div>
                                    <h3 className="text-lg font-bold">
                                        {catalogPickerType === 'system' ? 'Catálogo Global (Sistema)' : 'Mis Ítems de Empresa'}
                                    </h3>
                                    <p className="text-xs text-gray-500">Seleccione ítems para agregar al plan</p>
                                </div>
                                <div className="flex bg-slate-100 p-1 rounded-lg mr-4">
                                    <button
                                        onClick={() => setCatalogPickerType('system')}
                                        className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider transition-all ${catalogPickerType === 'system' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        Sistema
                                    </button>
                                    <button
                                        onClick={() => setCatalogPickerType('company')}
                                        className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider transition-all ${catalogPickerType === 'company' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        Mios
                                    </button>
                                </div>
                                <button onClick={() => setIsCatalogPickerOpen(false)} className="p-1 hover:bg-gray-100 rounded-full">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-4 border-b bg-gray-50">
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                    <Input
                                        placeholder="Buscar por código, nombre o categoría..."
                                        className="pl-10 bg-white"
                                        value={pickerSearch}
                                        onChange={(e) => setPickerSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-0">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-700 text-xs uppercase sticky top-0 bg-gray-50 z-10 shadow-sm">
                                        <tr>
                                            <th className="px-4 py-3 border-b">Código</th>
                                            <th className="px-4 py-3 border-b">Nombre</th>
                                            <th className="px-4 py-3 border-b">Categoría</th>
                                            <th className="px-4 py-3 border-b text-center">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {pickerItems
                                            .filter(i => catalogPickerType === 'system' ? !i.empresa_id : i.empresa_id !== null)
                                            .filter(i =>
                                                i.nombre.toLowerCase().includes(pickerSearch.toLowerCase()) ||
                                                i.codigo_interno.toLowerCase().includes(pickerSearch.toLowerCase()) ||
                                                i.categoria.toLowerCase().includes(pickerSearch.toLowerCase())
                                            )
                                            .map((item) => {
                                                const existingPlanItem = catalogoItems.find(cat => cat.item_catalogo_id === item.id);
                                                const isAlreadyAdded = !!existingPlanItem;

                                                // Verificar en qué rutinas está asignado si ya está en el plan
                                                const assignedRoutines = isAlreadyAdded ? fields.filter(f => f.id_bd).filter(rutina => {
                                                    const rId = rutina.id_bd as number;
                                                    const matrixKey = `${rId}-${existingPlanItem.id}`;
                                                    const isLocallySet = matrixChanges[matrixKey]?.aplica;
                                                    const dbDetail = planDetalles[rId]?.find((d: any) => d.plan_item_id === existingPlanItem.id);
                                                    const isDbSet = dbDetail?.aplica;
                                                    return matrixChanges[matrixKey] !== undefined ? isLocallySet : isDbSet;
                                                }) : [];

                                                return (
                                                    <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                                                        <td className="px-4 py-2 font-medium w-32">{item.codigo_interno}</td>
                                                        <td className="px-4 py-2">
                                                            <div className="font-medium text-slate-800">{item.nombre}</div>
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase">{item.unidad_medida}</span>
                                                                {assignedRoutines.length > 0 && assignedRoutines.map(r => (
                                                                    <span key={r.id_bd} className={`px-1 rounded text-[8px] font-black border ${r.tipo === 'M1' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                                                        r.tipo === 'M2' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                                                                            'bg-red-50 text-red-700 border-red-100'
                                                                        }`}>
                                                                        {r.tipo}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 uppercase tracking-tighter shadow-sm border border-slate-200">
                                                                {item.categoria}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 text-center w-24">
                                                            {isAlreadyAdded ? (
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <div className="text-[9px] font-black text-green-600 uppercase tracking-widest bg-green-50 px-2 py-1 rounded-md border border-green-100 shadow-sm w-full text-center">
                                                                        Vínculado
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    className="w-full h-8 text-[10px] font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700 shadow-sm shadow-indigo-100 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                                                    onClick={() => handleAddFromCatalog(item)}
                                                                >
                                                                    Vincular
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        {pickerItems.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                                                    Cargando catálogo o vacío...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="p-3 border-t bg-gray-50 text-right">
                                <Button variant="outline" size="sm" onClick={() => setIsCatalogPickerOpen(false)}>Cerrar</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirmación de Eliminación */}
                {itemToDelete && (
                    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
                            <div className="p-8 text-center">
                                <div className="mx-auto w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mb-6">
                                    <Trash2 className="w-8 h-8 text-rose-500" />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-wider mb-2">¿Eliminar ítem?</h3>
                                <p className="text-slate-500 text-sm font-medium leading-relaxed">
                                    Esta acción eliminará <span className="text-slate-900 font-bold">"{itemToDelete.nombre}"</span> de todas las rutinas de este plan.
                                </p>
                            </div>
                            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="flex-1 h-12 font-black text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 hover:bg-slate-200/50"
                                    onClick={() => setItemToDelete(null)}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="button"
                                    className="flex-1 h-12 bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-200 font-black text-xs uppercase tracking-widest border-none transition-all hover:scale-[1.02] active:scale-[0.98]"
                                    onClick={() => deletePlanItem(itemToDelete.id)}
                                >
                                    Eliminar
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

